import {Component, OnInit} from '@angular/core';
import {NgForOf, NgIf, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {SharedModule} from '../../../../../shared/shared.module';
import {EmployeeService} from '../../../../../shared/services/employee.service';
import {ActivatedRoute, Router} from '@angular/router';
import {AlertService} from '../../../../../shared/services/alert.service';
import {AuthService} from '../../../../../shared/services/auth.service';
import {FinalExamEvaluatorSubjectService} from '../../../../../shared/services/final-exam-evaluator-subject.service';
import {MatTooltip} from '@angular/material/tooltip';
import {Helper} from '../../../../../shared/constants/helper';
import {PermissionConstant} from '../../../../../shared/constants/permission-constant';

interface MarkCell {
  max: number;
  min: number;
  awarded: number;
  inputKey: string;
}

interface ProcessedSubject {
  code: string;
  name: string;
  type: string;
  credit: string;
  grade: string;
  status: string;
  markMap: {[key: string]: MarkCell};
  totalAwarded: number;
  totalMax: number;
}

interface MarkField {
  key: string;
  label: string;
}

@Component({
  selector: 'vex-update-result',
  standalone: true,
  imports: [FormsModule, NgForOf, NgIf, NgClass, SharedModule, MatTooltip],
  templateUrl: './update-result.component.html',
  styleUrl: './update-result.component.scss'
})
export class UpdateResultComponent implements OnInit {
  user: any;
  id: any;
  sessionId: any;
  semesterYear: any;
  resultType: any;
  loader = false;
  results: any;
  processedSubjects: ProcessedSubject[] = [];
  activeMarkFields: MarkField[] = [];
  newMarks: {[key: string]: any} = {};
  remarks: string = '';
  examinationSession: string = '';

  private readonly allMarkFields: MarkField[] = [
    {key: 'mst_1_marks', label: 'MST 1'},
    {key: 'mst_2_marks', label: 'MST 2'},
    {key: 'assignment_marks', label: 'Assignment'},
    {key: 'attendance_marks', label: 'Attendance'},
    {key: 'continuous_marks', label: 'Continuous'},
    {key: 'internal_marks', label: 'Internal'},
    {key: 'internal_theory_marks', label: 'Int. Theory'},
    {key: 'internal_practical_marks', label: 'Int. Practical'},
    {key: 'external_marks', label: 'External'},
    {key: 'external_theory_marks', label: 'Ext. Theory'},
    {key: 'external_practical_marks', label: 'Ext. Practical'},
    {key: 'external_viva_marks', label: 'Ext. Viva'},
  ];

  constructor(
    private employeeService: EmployeeService,
    private router: Router,
    public alertService: AlertService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private finalExamEvaluatorSubjectService: FinalExamEvaluatorSubjectService
  ) {
    this.authService.self().subscribe((data: any) => {
      if (data && data.id) {
        this.user = data;
        if (this.user?.user_type === 'EMPLOYEE') {
          this.getUserPermission();
        } else if (this.user?.user_type === 'SUPER_ADMIN' || this.user?.user_type === 'ADMIN') {
          this.loadFromRoute();
        } else {
          this.router.navigateByUrl('/home');
        }
      }
    });
  }

  ngOnInit(): void {}

  private loadFromRoute(): void {
    this.route.params.subscribe((params: any) => {
      if (params?.id && params?.session_id && params?.value && params?.result_type) {
        this.id = params.id;
        this.sessionId = params.session_id;
        this.semesterYear = params.value;
        this.resultType = params.result_type;
        this.get();
      }
    });
  }

  getUserPermission(): void {
    this.employeeService.detail(this.user.unique_id).subscribe((data: any) => {
      if (data?.employee_details?.designation?.designation_permissions) {
        const permissionData = data.employee_details.designation.designation_permissions;
        let rolePermissions: any[] = [];
        permissionData.forEach((sp: any) => {
          if (sp?.sub_permission?.meta) rolePermissions.push(sp.sub_permission.meta);
        });
        const canAccess = this.user?.designation_status === 'COE' ||
          rolePermissions.includes(PermissionConstant.RESULTS_PREVIOUS_RESULT_STUDENT_UPDATE_RESULT);
        if (!canAccess) {
          this.router.navigateByUrl('/home');
          return;
        }
        this.loadFromRoute();
      }
    });
  }

  get(): void {
    const key = this.resultType === 'SEMESTER' ? 'semester' : 'year';
    const params: any = {session_id: this.sessionId ?? '', [key]: this.semesterYear ?? ''};
    this.results = null;
    this.processedSubjects = [];
    this.activeMarkFields = [];
    this.loader = true;
    this.finalExamEvaluatorSubjectService.viewResult(params, this.id).subscribe(
      (data: any) => {
        if (data?.student_result_details && data?.student_data) {
          this.results = data;
          this.processSubjects();
        }
        this.loader = false;
      },
      () => {this.loader = false;}
    );
  }

  clampMark(inputKey: string, max: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = parseFloat(input.value);
    if (!isNaN(val)) {
      if (val > max) {
        this.newMarks[inputKey] = max;
        input.value = String(max);
      } else if (val < 0) {
        this.newMarks[inputKey] = 0;
        input.value = '0';
      } else {
        this.newMarks[inputKey] = val;
      }
    }
  }

  update(): void {
    if (!this.examinationSession || !this.examinationSession.trim()) {
      this.alertService.showErrors('Please enter examination session.');
      return;
    }
    const updatedMarks: {[key: string]: any} = {};
    this.processedSubjects.forEach((subject, idx) => {
      Object.entries(subject.markMap).forEach(([markType, mark]) => {
        const revised = this.newMarks[mark.inputKey];
        if (revised !== undefined && revised !== null && revised !== '' && parseFloat(revised) !== mark.awarded) {
          if (!updatedMarks[idx]) updatedMarks[idx] = {};
          updatedMarks[idx][markType] = revised;
        }
      });
    });
    if (!Object.keys(updatedMarks).length) {
      this.alertService.showErrors('No marks have been changed.');
      return;
    }
    const key = this.resultType === 'SEMESTER' ? 'semester' : 'year';
    const payload: any = {
      session_id: this.sessionId,
      [key]: this.semesterYear,
      updated_marks: updatedMarks,
      examination_session: this.examinationSession,
      remarks: this.remarks,
    };
    this.loader = true;
    this.finalExamEvaluatorSubjectService.updateStudentResult(payload, this.id).subscribe(
      () => {
        this.loader = false;
        this.alertService.showSuccess('Result updated successfully.');
        this.get();
      },
      () => {this.loader = false;}
    );
  }

  getRevisedTotal(subject: ProcessedSubject): number {
    return Object.values(subject.markMap).reduce((sum, mark) => {
      const revised = this.newMarks[mark.inputKey];
      const val = (revised !== undefined && revised !== null && revised !== '') ? parseFloat(revised) : mark.awarded;
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }

  private processSubjects(): void {
    const subjects: any[] = this.results?.student_result_details?.student_result || [];
    const activeMfSet = new Set<string>();

    this.processedSubjects = subjects.map((subject: any, idx: number) => {
      const markMap: {[key: string]: MarkCell} = {};
      for (const mf of this.allMarkFields) {
        if (subject[mf.key] && parseFloat(subject[mf.key]?.max_marks || 0) > 0) {
          activeMfSet.add(mf.key);
          markMap[mf.key] = {
            max: parseFloat(subject[mf.key].max_marks || 0),
            min: parseFloat(subject[mf.key].min_marks || 0),
            awarded: parseFloat(subject[mf.key].awarded_marks || 0),
            inputKey: `${idx}_${mf.key}`,
          };
        }
      }
      const cells = Object.values(markMap);
      const totalAwarded = cells.reduce((sum, m) => sum + m.awarded, 0);
      const totalMax = cells.reduce((sum, m) => sum + m.max, 0);
      return {
        code: subject.code || '',
        name: subject.subject_name || subject.name || '',
        type: Helper.getSubjectType(subject.type || ''),
        credit: subject.credit || '0',
        grade: subject.grade || '-',
        status: subject.status || '-',
        markMap,
        totalAwarded,
        totalMax,
      };
    });

    this.activeMarkFields = this.allMarkFields.filter(mf => activeMfSet.has(mf.key));
  }
}
